import AppComponent from '../base/app-component.ts';
import type { DataTable } from './data-table.ts';
import { parseDataNascita } from '../lib/parse-data-nascita.ts';

// --- Configurazione colonne ---

const COLUMNS = [
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}', sortable: true },
  { key: 'lavoratore.codiceFiscale', label: 'Codice Fiscale', type: 'code', sortable: true },
  { key: 'dataNascitaLavoratore', label: 'Data nascita', type: 'date', sortable: true, emptyText: '-' },
  { key: 'cassa', label: 'Cassa', type: 'badge', badgeFilter: 'badge_cassa', sortable: true },
  { key: 'stato', label: 'Stato', type: 'badge', badgeFilter: 'stato_delega_badge', badgeTextFilter: 'stato_label', sortable: true },
  { key: 'dataPresentazione', label: 'Data presentaz.', type: 'date', sortable: true },
  { key: 'note', label: 'Note', type: 'text', truncate: 30, emptyText: '-' },
];

const ACTIONS = [
  { name: 'rimuovi', icon: 'x-lg', btnClass: 'btn-outline-danger', title: 'Rimuovi dalla presentazione', perm: 'operatore,amministratore' },
];

// --- Componente ---

export class DettaglioPresentazione extends AppComponent {
  static override componentName = 'wc-dettaglio-presentazione';

  protected override get templateId() { return 'dettaglio-presentazione'; }

  private codaId = 0;
  private dataTable: DataTable | null = null;
  private allDeleghe: Record<string, unknown>[] = [];
  private statoFiltro = '';
  private filterText = '';

  // Dati coda per rendering
  private codaData: { cassa?: string; dataPresentazione?: string; totale?: number } = {};

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      loading: !this.codaData.cassa,
      columnsJson: JSON.stringify(COLUMNS),
      actionsJson: JSON.stringify(ACTIONS),
      cassa: this.codaData.cassa ?? '',
      dataPresentazione: this.codaData.dataPresentazione ?? '',
      totale: this.codaData.totale ?? 0,
    };
  }

  protected override afterRender(): void {
    this.codaId = this.attributeNumber('coda-id');

    // Se non abbiamo ancora i dati coda, carichiamo tutto
    if (!this.codaData.cassa) {
      this.caricaDettaglio();
      return;
    }

    // Dopo il re-render con i dati coda
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltroRapido();
    this.setupFiltroStato();
    this.setupScaricaCsv();
    this.setupEliminaPresentazione();
    this.caricaDeleghe();
  }

  // --- Caricamento ---

  private async caricaDettaglio(): Promise<void> {
    const result = await this.api.code.dettaglio(this.codaId);
    if (!result.ok) {
      this.codaData = {};
      this.innerHTML = '';
      this.render();
      return;
    }

    const coda = result.data;
    this.codaData = {
      cassa: (coda as Record<string, unknown>).cassa as string,
      dataPresentazione: (coda as Record<string, unknown>).dataPresentazione as string,
      totale: (coda as Record<string, unknown>).numDeleghe as number ?? 0,
    };
    // Re-render con i dati coda
    this.render();
  }

  private async caricaDeleghe(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const result = await this.api.code.deleghe(this.codaId);
    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { deleghe, count } = result.data;
    this.allDeleghe = deleghe as Record<string, unknown>[];
    this.codaData.totale = count;
    this.updateHeaderCount(count);
    this.applyFilters();
  }

  // --- Filtro rapido ---

  private setupFiltroRapido(): void {
    if (!this.dataTable) return;
    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.filterText = ((e as CustomEvent).detail.filter ?? '').toUpperCase();
      this.applyFilters();
    });
  }

  // --- Filtro stato ---

  private setupFiltroStato(): void {
    this.delegate.on('change', '[data-filtro-stato]', () => {
      const select = this.$('[data-filtro-stato]') as HTMLSelectElement | null;
      this.statoFiltro = select?.value || '';
      this.applyFilters();
    });
  }

  private applyFilters(): void {
    if (!this.dataTable) return;
    let filtered = this.allDeleghe;

    if (this.statoFiltro) {
      filtered = filtered.filter(d => d.stato === this.statoFiltro);
    }

    if (this.filterText) {
      const q = this.filterText;
      const dataIso = parseDataNascita(q);
      filtered = filtered.filter(d => {
        if (dataIso) return (d.dataNascitaLavoratore as string) === dataIso;
        const lav = d.lavoratore as Record<string, unknown> | undefined;
        const cognome = ((lav?.cognome as string) ?? '').toUpperCase();
        const nome = ((lav?.nome as string) ?? '').toUpperCase();
        const cf = ((lav?.codiceFiscale as string) ?? '').toUpperCase();
        return cognome.includes(q) || nome.includes(q) || cf.includes(q);
      });
    }

    this.dataTable.setData(filtered, { page: 1, totalPages: 1, count: filtered.length, pageSize: filtered.length });
  }

  // --- Eventi data-table ---

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'rimuovi') this.handleRimuovi(row);
    });
  }

  // --- Azione: Rimuovi singola delega ---

  private async handleRimuovi(row: Record<string, unknown>): Promise<void> {
    const lav = row.lavoratore as Record<string, unknown> | undefined;
    const nome = `${lav?.cognome ?? ''} ${lav?.nome ?? ''}`.trim();
    const ok = await Weext.confirm(`Rimuovere la delega di ${nome}? Tornera' DA_PRESENTARE.`, {
      title: 'Conferma rimozione',
      confirmText: 'Rimuovi',
      confirmClass: 'btn-danger',
    });
    if (!ok) return;

    const result = await this.api.code.eliminaDelega(this.codaId, row.id as number);
    if (result.ok) {
      Weext.toast('Delega rimossa dalla presentazione', { type: 'success' });
      this.caricaDeleghe();
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  // --- Azione: Scarica CSV ---

  private setupScaricaCsv(): void {
    this.delegate.on('click', '[data-action="scarica-csv"]', (e: Event) => {
      e.preventDefault();
      if (this.codaId) window.open(`/api/presentazioni/${this.codaId}/export-csv`, '_blank');
    });
  }

  // --- Azione: Elimina intera presentazione ---

  private setupEliminaPresentazione(): void {
    this.delegate.on('click', '[data-action="elimina-presentazione"]', () => this.handleEliminaPresentazione());
  }

  private async handleEliminaPresentazione(): Promise<void> {
    const totale = this.codaData.totale ?? 0;
    const ok = await Weext.confirm(
      `Eliminare la presentazione? Le ${totale} deleghe torneranno DA_PRESENTARE.`,
      {
        title: 'Conferma eliminazione',
        confirmText: 'Elimina',
        confirmClass: 'btn-danger',
      },
    );
    if (!ok) return;

    const result = await this.api.code.elimina(this.codaId);
    if (result.ok) {
      Weext.toast('Presentazione eliminata', { type: 'success' });
      window.location.href = '/presentate';
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  // --- Helper ---

  private updateHeaderCount(count: number): void {
    const headerInfo = this.$('[data-header-info]');
    if (!headerInfo) return;
    const countSpan = headerInfo.querySelector('.text-muted');
    if (countSpan) countSpan.textContent = `${count} deleghe`;
  }
}
